/**
 * Products finance hub card/modal UI — consumes shared hub context when inside a provider.
 */
import React, { useContext, useEffect } from 'react';
import { Button, Empty } from 'antd';
import { CaretDown, Package, Warning } from '@phosphor-icons/react';
import { ProductsFinanceHubContext, ProductsFinanceHubProvider } from './ProductsFinanceHubContext';
import { ProductFinanceHubRow } from './ProductFinanceHubRow';
import './oneButton-css/ProductsFinanceHubSection.css';
import type { ProductsFinanceHubSectionProps } from './ProductsFinanceHubSection.types';

const ProductsFinanceHubSectionInner: React.FC<Omit<ProductsFinanceHubSectionProps, 'salesForceId'>> = ({
    embeddedInModal = false,
}) => {
    const ctx = useContext(ProductsFinanceHubContext);

    useEffect(() => {
        if (embeddedInModal && ctx) ctx.setHubOpen(true);
    }, [embeddedInModal, ctx]);

    if (!ctx) {
        return null;
    }

    const {
        theme,
        rows,
        hubProductOrder,
        isOpportunityInfoReadOnly,
        isOpportunityInfoDisabled,
        installStateCode,
        validationError,
        saveLoading,
        svaE4ExcelBusy,
        handleSaveProducts,
        opportunityId,
        hubOpen,
        setHubOpen,
        opportunityLoadError,
        isOpportunityInitializing,
    } = ctx;

    const currentlyLoading = isOpportunityInitializing;
    // If rows are empty, AND we are NOT currently loading, then it's definitively an error.
    const isError = opportunityLoadError || (!rows.length && !currentlyLoading);

    return (
        <div
            className={
                embeddedInModal
                    ? `obd-products-hub obd-products-hub--embedded obd-theme-${theme} ${isOpportunityInfoDisabled ? 'obd-disabled-section' : ''}`
                    : `obd-card obd-card--products-hub obd-theme-${theme} ${isOpportunityInfoDisabled ? 'obd-disabled-section' : ''}`
            }
        >
            {!embeddedInModal && (
                <button type="button" className="obd-products-hub__head" onClick={() => setHubOpen((o) => !o)}>
                    <span className="obd-products-hub__title">
                        <Package size={18} weight="duotone" className="obd-products-hub__title-icon" aria-hidden />
                        Products
                    </span>
                    <CaretDown
                        size={16}
                        weight="bold"
                        className={`obd-products-hub__chevron ${hubOpen ? 'obd-products-hub__chevron--open' : ''}`}
                    />
                </button>
            )}

            {(embeddedInModal || hubOpen) && (
                <div className="obd-products-hub__body">
                    {!rows.length && !isError && <p className="obd-bento-loading">Loading…</p>}
                    {!rows.length && isError && (
                        <p className="obd-bento-loading" style={{ color: '#ff4d4f', margin: 0, padding: '30px 0', fontWeight: 500 }}>
                            Opportunity and product details not Found
                        </p>
                    )}

                    {rows.length > 0 && (
                        <>
                            <p className="obd-products-hub__api-hint">
                                <strong>{installStateCode}</strong> — tap product to include/exclude; use menus for
                                partner &amp; method. Save updates all five lines.
                            </p>
                            <div className="obd-pf-rows-wrap">
                                <div className="obd-pf-rows-head" aria-hidden>
                                    <span className="obd-pf-rows-head__product">Product</span>
                                    <span className="obd-pf-rows-head__partner">Finance partner</span>
                                    <span className="obd-pf-rows-head__method">Finance method</span>
                                </div>
                                {hubProductOrder.map((name) => (
                                    <ProductFinanceHubRow key={name} productName={name} />
                                ))}
                            </div>
                            
                            <div className="obd-products-hub__save-block">
                                <p className="obd-products-hub__save-hint">
                                    Save writes <strong>all five</strong> product lines — each can have a different partner and
                                    method.
                                </p>
                                {validationError ? (
                                    <div className="obd-products-hub__validation-warning">
                                        <Warning size={16} weight="fill" />
                                        <span>{validationError}</span>
                                    </div>
                                ) : null}
                                <div className="obd-products-hub__save-row">
                                    <Button
                                        type="primary"
                                        className="obd-products-hub__save-all"
                                        loading={saveLoading}
                                        onClick={handleSaveProducts}
                                        disabled={isOpportunityInfoReadOnly || !opportunityId || svaE4ExcelBusy}
                                    >
                                        Save all product settings
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export const ProductsFinanceHubSection: React.FC<ProductsFinanceHubSectionProps> = ({
    salesForceId,
    ...rest
}) => {
    const existing = useContext(ProductsFinanceHubContext);

    if (existing) {
        return <ProductsFinanceHubSectionInner {...rest} />;
    }

    return (
        <ProductsFinanceHubProvider salesForceId={salesForceId}>
            <ProductsFinanceHubSectionInner {...rest} />
        </ProductsFinanceHubProvider>
    );
};

export type { ProductsFinanceHubSectionProps } from './ProductsFinanceHubSection.types';

export {
    ProductsFinanceHubContext,
    ProductsFinanceHubProvider,
    useProductsFinanceHubContext,
} from './ProductsFinanceHubContext';
export { ProductFinanceHubRow } from './ProductFinanceHubRow';

export default ProductsFinanceHubSection;
